import {
  Box,
  Button,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { MdCancel } from "react-icons/md";
import {
  useClearRefinements,
  useCurrentRefinements,
  useInfiniteHits,
  useInstantSearch,
  useMenu,
  useSearchBox,
} from "react-instantsearch";
import { AlgoliaSortOrder } from "@common/interfaces/Algolia";

export const statusOptions = [
  {
    label: "Upcoming",
    value: "OPEN",
  },
  {
    label: "Unannounced",
    value: "PENDING_ANNOUNCEMENT",
  },
  {
    label: "Pending Approval",
    value: "PENDING_APPROVAL",
  },
  {
    label: "Draft",
    value: "DRAFT",
  },
  {
    label: "Past Events",
    value: "OFFSALE",
  },
  {
    label: "Canceled",
    value: "CANCELED",
  },
  {
    label: "All Events",
    value: "ALL",
  },
];

export const labelForStatus = (status: string) => {
  return statusOptions.find((s) => s.value === status)?.label || "";
};

const EventFilters = ({
  onChangeSort,
}: {
  onChangeSort: (sortOrder: AlgoliaSortOrder) => void;
}) => {
  const { status } = useInstantSearch();
  const {
    results: { hierarchicalFacets },
  } = useInfiniteHits();

  const [globalFacetCounts, setGlobalFacetCounts] = useState<Record<string, number>>({});

  const { items: menuItems, refine: refineParameters } = useMenu({
    attribute: "status",
  });

  const facetsByStatus = useMemo(() => {
    const allStatusCounts = {} as Record<string, number>;
    // If we have menu items, override with actual counts
    if (menuItems && menuItems.length > 0) {
      menuItems.forEach(item => {
        allStatusCounts[item.value] = item.count;
      });
      setGlobalFacetCounts(allStatusCounts);
    } else if (globalFacetCounts) {
      // Otherwise use global counts as fallback
      Object.keys(globalFacetCounts).forEach(key => {
        allStatusCounts[key] = globalFacetCounts[key];
      });
    }

    return allStatusCounts;
  }, [menuItems]);

  const { refine, query } = useSearchBox();
  const { refine: clearRefinements } = useClearRefinements({
    includedAttributes: ["status"],
  });
  const { items: refinements } = useCurrentRefinements();
  const currentFilter = useMemo(() => {
    const status = refinements.find((r) => r.attribute === "status");

    if (!status) return "ALL";

    return (
      (status.refinements.find((r) => r.attribute === "status")
        ?.value as string) || "ALL"
    );
  }, [refinements, query]);

  return (
    <>
      <Stack
        justify={"space-between"}
        w="full"
        direction={["column", "row"]}
        spacing={[4, 2]}
      >
        <VStack flexGrow={1} w="full">
          <InputGroup>
            <Input
              placeholder="Search for events or venues..."
              value={query}
              onChange={(e) => refine(e.target.value)}
            />
            <InputRightElement>
              {query ? (
                <IconButton
                  isLoading={status === "loading"}
                  isDisabled={query === ""}
                  disabled={query === ""}
                  variant={"ghost"}
                  background="transparent"
                  aria-label="Cancel"
                  icon={<MdCancel />}
                  onClick={() => refine("")}
                />
              ) : null}
            </InputRightElement>
          </InputGroup>
          <HStack w="full" overflowX={["auto"]} py={[2, 0]}>
            {statusOptions.map((s) => (
              <Button
                key={s.value}
                size="sm"
                flexShrink={0}
                flexGrow={0}
                background={s.value === currentFilter ? "gray.800" : "gray.100"}
                color={s.value === currentFilter ? "white" : "gray.800"}
                _hover={{
                  background:
                    s.value === currentFilter ? "gray.800" : "gray.100",
                  color: s.value === currentFilter ? "white" : "gray.800",
                }}
                fontWeight={s.value === currentFilter ? "bold" : "normal"}
                onClick={() => {
                  if (s.value === "ALL") {
                    clearRefinements();
                  } else {
                    refineParameters(s.value);
                    onChangeSort(
                      s.value === "OFFSALE" ? "descending" : "ascending"
                    );
                  }
                }}
              >
                <HStack>
                  <Text>{s.label}</Text>

                  {facetsByStatus[s.value] && (
                    <Box
                      as="span"
                      fontSize="xs"
                      rounded="xl"
                      background="white"
                      color="gray.500"
                      p={1}
                      lineHeight="1"
                      h={4}
                      py={0.5}
                    >
                      {facetsByStatus[s.value]}
                    </Box>
                  )}
                </HStack>
              </Button>
            ))}
          </HStack>
        </VStack>
      </Stack>
    </>
  );
};

export default EventFilters;
